import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import theme from "../../../theme";
import { Box } from "../../../components/Box";
import { Flex } from "../../../components/Flex";
import { H2 } from "../../../components/Heading";
import { Paragraph } from "../../../components/Paragraph";
import Select from "../../../components/Select";
import { notify } from "../../../utils/notify";
import { showError } from "../../../utils/error-handling";
import { queryClient } from "../../../queryClient";
import {
  CountryRisklevel,
  useGetCountryRiskLevel,
} from "../../../data/useGetCountryRiskLevel";
import { useEditCountryRiskLevel } from "../../../data/useEditCountryRiskLevel";

type RiskLower = "low" | "medium" | "high";

type ApiRow = {
  id: number;
  country: { id: number; name: string };
  risk_level: RiskLower | string;
  updated_at?: string;
};

const COLS = "minmax(220px,0.7fr) auto auto";

const baseRow: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: COLS,
  alignItems: "center",
  columnGap: 24,
};
const headStyle: React.CSSProperties = {
  ...baseRow,
  padding: "10px 16px",
  color: theme.colors.muted,
};
const bodyRowStyle: React.CSSProperties = {
  ...baseRow,
  padding: "14px 16px",
  borderTop: "1px solid #F3F4F6",
};

const toRow = (r: CountryRisklevel): ApiRow => ({
  id: r.id,
  country: {
    id: r.country?.id,
    name: r.country?.name,
  },
  risk_level: (r.risk_level ?? "medium") as RiskLower,
  updated_at: r.updated_at,
});

const riskOptions = [
  { label: "Low", value: "low" },
  { label: "Medium", value: "medium" },
  { label: "High", value: "high" },
] as const;

const toLowerRisk = (v?: string): RiskLower =>
  (v?.toLowerCase() as RiskLower) ?? "medium";

const formatLastUpdated = (value?: string) => {
  if (!value) return "-";
  const asISO = DateTime.fromISO(value);
  if (asISO.isValid) return asISO.toFormat("dd LLL yyyy");
  return "-";
};

const SettingsPropertiesCountry = () => {
  const { data, isLoading, isError } = useGetCountryRiskLevel();
  const { mutate: editCountryRiskLevel } = useEditCountryRiskLevel();

  const items = useMemo<ApiRow[]>(
    () => (Array.isArray(data) ? (data as CountryRisklevel[]).map(toRow) : []),
    [data]
  );
  const [list, setList] = useState<ApiRow[]>([]);
  useEffect(() => setList(items), [items]);

  const invalidate = () =>
    queryClient.invalidateQueries({
      predicate: (q) =>
        Array.isArray(q.queryKey) && q.queryKey[0] === "country_risk_levels",
    });

  const onRiskChange = (rowId: number, nextLower: RiskLower) => {
    const target = list.find((r) => r.id === rowId);
    if (!target) return;

    const curLower = toLowerRisk(target.risk_level as string);
    if (curLower === nextLower) return;

    setList((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, risk_level: nextLower } : r))
    );

    editCountryRiskLevel(
      {
        id: rowId,
        params: {
          country: target.country.id,
          risk_level: nextLower.toLowerCase(),
        },
      } as any,
      {
        onSuccess: () => {
          notify("Risk level updated!", {
            type: "success",
            position: "bottom-left",
          });
          invalidate();
        },
        onError: (err) => {
          setList((prev) =>
            prev.map((r) =>
              r.id === rowId ? { ...r, risk_level: target.risk_level } : r
            )
          );
          showError(err, "Failed to update risk level.");
        },
      }
    );
  };

  return (
    <Box bg="white" borderRadius="16px" p={5} overflow="hidden">
      <Flex justifyContent="space-between" alignItems="center">
        <Box>
          <H2 fontSize="3">Country</H2>
          <Paragraph color="gray.60">
            Configure for capturing previous income and employment history here.
          </Paragraph>
        </Box>
        <span />
      </Flex>

      <div style={headStyle}>
        <Paragraph color="gray.60">Country</Paragraph>
        <Paragraph color="gray.60">Risk level</Paragraph>
        <Paragraph color="gray.60">Last updated</Paragraph>
      </div>

      {isLoading ? (
        <Paragraph color="gray.60" mt={3}>
          Loading…
        </Paragraph>
      ) : isError ? (
        <Paragraph color="red.60" mt={3}>
          Failed to load countries.
        </Paragraph>
      ) : list.length === 0 ? (
        <Paragraph color="gray.60" px={4} py={4}>
          No countries found.
        </Paragraph>
      ) : (
        <div style={{ borderTop: "1px solid #F3F4F6" }}>
          {list.map((row) => {
            const current = riskOptions.find(
              (o) => o.value === toLowerRisk(row.risk_level as string)
            );
            return (
              <div key={row.id} style={bodyRowStyle}>
                <Paragraph fontSize="2" fontWeight="600">
                  {row.country?.name ?? "-"}
                </Paragraph>

                <Select
                  options={riskOptions as any}
                  value={current}
                  onChange={(opt: any) =>
                    onRiskChange(row.id, (opt?.value ?? "medium") as RiskLower)
                  }
                  width={220}
                />

                <Paragraph color="gray.80">
                  {formatLastUpdated(row.updated_at)}
                </Paragraph>
              </div>
            );
          })}
        </div>
      )}
    </Box>
  );
};

export default SettingsPropertiesCountry;
